import React, { useEffect, useState } from "react";
import { RiPencilFill } from "react-icons/ri";
import { Modal } from "@/components/Modal";
import { CompanyForm } from "./CompanyForm";
import { useCompanyStore } from "@/store/companyStore";
import { EditCompanyFormData, Role } from "./type";
import {
  ApiErrorResponse,
  BusinessTypeEnum,
  CompanyDto,
  CompanyParams,
} from "@/types";
import { LoadingSpinner } from "@/components/Loading";
import { useLoadingStore } from "@/store/loadingStore";

interface Props {
  children?: React.ReactNode;
  id: number;
  className?: string;
  defaultCompany?: CompanyDto | null;
  restrictFields?: boolean;
}
export const EditCompany = React.memo(
  ({ children, id, defaultCompany, className, restrictFields = false }: Props) => {
    const [open, setOpen] = useState(false);
    const [serverError, setServerError] = useState<ApiErrorResponse | null>(
      null
    );
    const {
      loadingCompany,
      company,
      fetchCompanyById,
      updateCompany,
      fetchMyCompany,
    } = useCompanyStore();
    const { setLoading, isLoading: isLoadingUpdate } = useLoadingStore();

    useEffect(() => {
      if (!open) return;
      if (!defaultCompany) {
        fetchCompanyById(id);
      }
    }, [id, open, defaultCompany]);

    useEffect(() => {
      if (!open) setServerError(null);
    }, [open]);

    const openModal = () => {
      setOpen(true);
    };

    const closeModal = () => {
      setOpen(false);
    };

    const sourceCompany = defaultCompany || company;

    const handleSubmit = async (data: EditCompanyFormData) => {
      setLoading(true);
      const isMemberType = data.role === Role.MEMBER;
      const companyParams = {
        business_type: isMemberType
          ? BusinessTypeEnum.MEMBER
          : BusinessTypeEnum.NON_MEMBER,
        name: data.companyData.corporateName?.trim(),
        business_code: data.companyData.companyCode,
        corporate_code: data.companyData.corporateCode || null,
        series: isMemberType ? data.companyData.series : null,
        business: isMemberType ? data.companyData.business : null,
        prefecture_id: data.companyData.prefecture
          ? data.companyData.prefecture
          : null,
        post_code: data.companyData.postCode ? data.companyData.postCode : null,
        representative_position: isMemberType
          ? data.companyData.representativePosition?.trim()
          : null,
        address: data.companyData.address?.trim(),
        phone: data.companyData.phoneNumber
          ? data.companyData.phoneNumber.replace(/[^\d-]/g, "")
          : null,
        email: data.companyData.email || null,
        joining_date: isMemberType
          ? data.companyData.joiningDate || null
          : null,
        unsubscribe_date: isMemberType
          ? data.companyData.unSubscribingDate || null
          : null,
        store_display_name: isMemberType
          ? data.companyData.storeName?.trim()
          : null,
        note: data.companyData.notes?.trim(),
      } as CompanyParams;
      try {
        await updateCompany(id, companyParams);
        setServerError({ error: null } as unknown as ApiErrorResponse);
        closeModal();
        setLoading(false);
        await fetchMyCompany();
      } catch (error) {
        setServerError(error as ApiErrorResponse);
        setLoading(false);
      }
    };

    return (
      <>
        <div
          onClick={(e) => {
            e.stopPropagation();
            openModal();
          }}
          className={className}
        >
          {children || (
            <RiPencilFill className="w-5 h-5 text-grey-600/80 hover:text-cyan cursor-pointer" />
          )}
        </div>
        <Modal key={id} title="事業所情報" open={open} onOpenChange={setOpen}>
          {loadingCompany ? (
            <div className="h-[60vh] flex items-center justify-center">
              <LoadingSpinner />
            </div>
          ) : (
            <CompanyForm
              type="edit"
              restrictFields={restrictFields}
              defaultValues={{
                role: sourceCompany?.business_type as any,
                companyData: {
                  corporateName: sourceCompany?.name || "",
                  prefecture: sourceCompany?.prefecture_id || "",
                  postCode: sourceCompany?.post_code || "",
                  address: sourceCompany?.address || "",
                  companyCode: sourceCompany?.business_code || "",
                  corporateCode: sourceCompany?.corporate_code || "",
                  series: sourceCompany?.series || "",
                  business: sourceCompany?.business || "",
                  notes: sourceCompany?.note || "",
                  representativePosition:
                    sourceCompany?.representative_position || "",
                  storeName: sourceCompany?.store_display_name || "",
                  joiningDate: sourceCompany?.joining_date || "",
                  unSubscribingDate: sourceCompany?.unsubscribe_date || "",
                  phoneNumber: sourceCompany?.phone || "",
                  email: sourceCompany?.email || "",
                },
              }}
              isLoading={isLoadingUpdate}
              serverError={serverError}
              onSubmit={handleSubmit}
              onCancel={closeModal}
            />
          )}
        </Modal>
      </>
    );
  }
);
